import type {
  AdminPlanStatsResponse,
  AdminUserStatsResponse,
  PublicStatsResponse,
} from '@pulsify/shared/dto'
import * as m from '~/paraglide/messages'
import { METRIC_COLORS } from '~/shared/styles/chart-palette'
import { ratio } from './stats-derive'
import { formatAverage, formatCount, formatShare } from './stats-format'
import { Figure, FigureGrid, MeterList, MeterRow, SplitBar } from './stats-tiles'

interface Props<T> {
  stats: T
  locale: string
}

export function ActivityFigures({ stats, locale }: Props<AdminUserStatsResponse>) {
  const total = stats.accounts.total
  const windows = [
    { key: '24h', label: m.statsWindow24h(), value: stats.active.last24h },
    { key: '7d', label: m.statsWindow7d(), value: stats.active.last7d },
    { key: '30d', label: m.statsWindow30d(), value: stats.active.last30d },
  ]
  const stickiness = ratio(stats.active.last24h, stats.active.last30d)

  return (
    <>
      <MeterList>
        {windows.map((window) => {
          const share = ratio(window.value, total)
          return (
            <MeterRow
              key={window.key}
              label={window.label}
              value={formatCount(window.value, locale)}
              share={share}
              shareText={formatShare(share, locale)}
              color={METRIC_COLORS.views}
            />
          )
        })}
      </MeterList>
      <div className="flex items-end justify-between gap-4 border-t pt-4">
        <div className="min-w-0 space-y-1">
          <p className="font-medium text-foreground text-sm">{m.statsStickiness()}</p>
          <p className="text-muted-foreground text-xs leading-relaxed">{m.statsStickinessHint()}</p>
        </div>
        <p className="shrink-0 font-semibold font-serif text-2xl text-foreground tabular-nums leading-none">
          {formatShare(stickiness, locale)}
        </p>
      </div>
    </>
  )
}

export function JoinerFigures({ stats, locale }: Props<AdminPlanStatsResponse>) {
  const { joinedLast7d, joinedLast30d, guestsJoinedLast7d } = stats.people
  const withAccount = Math.max(0, joinedLast7d - guestsJoinedLast7d)
  return (
    <>
      <FigureGrid>
        <Figure
          label={m.statsSeriesPeopleJoined()}
          value={formatCount(joinedLast7d, locale)}
          detail={m.statsCountIn30Days({ count: formatCount(joinedLast30d, locale) })}
        />
        <Figure
          label={m.statsWebGuests()}
          value={formatShare(ratio(guestsJoinedLast7d, joinedLast7d), locale)}
          detail={m.statsGuestsAmongThem({ count: formatCount(guestsJoinedLast7d, locale) })}
        />
      </FigureGrid>
      <SplitBar
        label={m.statsJoinersSplitLabel()}
        parts={[
          {
            key: 'accounts',
            label: m.statsJoinersAccounts(),
            value: withAccount,
            valueText: formatCount(withAccount, locale),
            color: METRIC_COLORS.likes,
          },
          {
            key: 'guests',
            label: m.statsWebGuests(),
            value: guestsJoinedLast7d,
            valueText: formatCount(guestsJoinedLast7d, locale),
            color: METRIC_COLORS.impressions,
          },
        ]}
      />
    </>
  )
}

export function UpcomingFigures({ stats, locale }: Props<AdminPlanStatsResponse>) {
  const { plans, people } = stats
  const linked = [
    { key: 'event', label: m.statsWithEvent(), value: plans.upcomingWithEvent },
    { key: 'place', label: m.statsWithPlace(), value: plans.upcomingWithListing },
  ]
  return (
    <>
      <FigureGrid className="sm:grid-cols-4">
        <Figure label={m.statsUpcomingPlans()} value={formatCount(plans.upcoming, locale)} />
        <Figure label={m.statsPlansToday()} value={formatCount(plans.happeningToday, locale)} />
        <Figure
          label={m.statsAveragePeople()}
          value={formatAverage(people.averagePerUpcomingPlan, locale)}
        />
        <Figure
          label={m.statsMedianPeople()}
          value={formatAverage(people.medianPerUpcomingPlan, locale)}
        />
      </FigureGrid>
      <SplitBar
        label={m.statsChatKindsLabel()}
        parts={[
          {
            key: 'quiet',
            label: m.statsOneToOneMeetups(),
            value: plans.upcomingQuietChat,
            valueText: formatCount(plans.upcomingQuietChat, locale),
            color: METRIC_COLORS.checkIns,
          },
          {
            key: 'open',
            label: m.statsGroupChats(),
            value: plans.upcomingOpenChat,
            valueText: formatCount(plans.upcomingOpenChat, locale),
            color: METRIC_COLORS.interactions,
          },
        ]}
      />
      <MeterList>
        {linked.map((row) => {
          const share = ratio(row.value, plans.upcoming)
          return (
            <MeterRow
              key={row.key}
              label={row.label}
              value={formatCount(row.value, locale)}
              share={share}
              shareText={formatShare(share, locale)}
              color={METRIC_COLORS.checkIns}
            />
          )
        })}
      </MeterList>
    </>
  )
}

export function InviteFigures({ stats, locale }: Props<AdminPlanStatsResponse>) {
  const { sentLast7d, acceptedLast7d, acceptedShareLast7d } = stats.invites
  return (
    <MeterList>
      <MeterRow
        label={m.statsInvitesSent()}
        value={formatCount(sentLast7d, locale)}
        share={sentLast7d > 0 ? 1 : null}
        color={METRIC_COLORS.interactions}
      />
      <MeterRow
        label={m.statsInvitesAccepted()}
        value={formatCount(acceptedLast7d, locale)}
        share={acceptedShareLast7d}
        shareText={formatShare(acceptedShareLast7d, locale)}
        color={METRIC_COLORS.interactions}
      />
    </MeterList>
  )
}

export function InviteRate({ stats, locale }: Props<AdminPlanStatsResponse>) {
  return (
    <div className="shrink-0 text-right">
      <p className="font-semibold font-serif text-2xl text-foreground tabular-nums leading-none">
        {formatShare(stats.invites.acceptedShareLast7d, locale)}
      </p>
      <p className="mt-1 text-muted-foreground text-xs">{m.statsAcceptanceRate()}</p>
    </div>
  )
}

export function ChatFigures({ stats, locale }: Props<AdminPlanStatsResponse>) {
  const { messagesLast24h, messagesLast7d, activeChatsLast7d } = stats.chat
  return (
    <FigureGrid className="sm:grid-cols-3">
      <Figure
        label={m.statsMessages()}
        value={formatCount(messagesLast7d, locale)}
        detail={m.statsCountIn24h({ count: formatCount(messagesLast24h, locale) })}
      />
      <Figure label={m.statsActiveChats()} value={formatCount(activeChatsLast7d, locale)} />
      <Figure
        label={m.statsMessagesPerChat()}
        value={formatAverage(ratio(messagesLast7d, activeChatsLast7d), locale)}
      />
    </FigureGrid>
  )
}

export function AssistantFigures({ stats, locale }: Props<AdminPlanStatsResponse>) {
  const { handledMessagesLast7d, actionsLast7d, undoneLast7d } = stats.assistant
  const perMessage = ratio(actionsLast7d, handledMessagesLast7d)
  const undoShare = ratio(undoneLast7d, handledMessagesLast7d)
  return (
    <FigureGrid className="sm:grid-cols-3">
      <Figure
        label={m.statsAssistantHandled()}
        value={formatCount(handledMessagesLast7d, locale)}
      />
      <Figure
        label={m.statsAssistantActions()}
        value={formatCount(actionsLast7d, locale)}
        detail={
          perMessage === null
            ? undefined
            : m.statsActionsPerMessage({ value: formatAverage(perMessage, locale) })
        }
      />
      <Figure
        label={m.statsAssistantUndone()}
        value={formatCount(undoneLast7d, locale)}
        detail={
          undoShare === null
            ? undefined
            : m.statsUndoShare({ share: formatShare(undoShare, locale) })
        }
      />
    </FigureGrid>
  )
}

export function PlatformFigures({
  stats,
  guests,
  locale,
}: Props<PublicStatsResponse> & { guests?: AdminUserStatsResponse['guests'] }) {
  return (
    <FigureGrid className="sm:grid-cols-4">
      <Figure label={m.statsBusinesses()} value={formatCount(stats.businesses, locale)} />
      <Figure label={m.statsPlaces()} value={formatCount(stats.listings, locale)} />
      <Figure label={m.statsUpcomingEvents()} value={formatCount(stats.upcomingEvents, locale)} />
      {guests && (
        <Figure
          label={m.statsWebGuests()}
          value={formatCount(guests.total, locale)}
          detail={m.statsNewIn7Days({ count: formatCount(guests.newLast7d, locale) })}
        />
      )}
    </FigureGrid>
  )
}
